import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import EyeIcon from '@mui/icons-material/VisibilityOutlined';
import { CircularProgress, IconButton, Stack, useTheme } from '@mui/material';

import { GetReportPdfMode, PlatformLocale, TestSessionStatus, useGetReportPdf } from '@/api/generated';
import DocumentDownloadIcon from '@/assets/icons/document-download.svg?react';
import { useSnackbar } from '@/hooks/useSnackbar';

type TestSessionButtonsProps = {
  testSession: { id?: string; status?: string } | null | undefined;
};

const TestSessionButtons = ({ testSession }: TestSessionButtonsProps) => {
  const { mutateAsync: getReportPdf, isLoading: isGettingReportPdf } = useGetReportPdf();
  const { spacing, palette } = useTheme();
  const { i18n, t } = useTranslation();
  const { showMessage } = useSnackbar();

  const doGetReportPdf = useCallback(
    async (mode: GetReportPdfMode, testSessionId: string) => {
      try {
        return await getReportPdf({
          testSessionId,
          params: { locale: i18n.language as PlatformLocale, mode }
        });
      } catch (error) {
        showMessage(t('error.somethingWentWrong', { ns: 'common' }));
        // eslint-disable-next-line no-console, i18next/no-literal-string
        console.error('Failed to fetch report PDF', error);

        return undefined;
      }
    },
    [getReportPdf, i18n.language, showMessage, t]
  );

  const handleDownload = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!testSession?.id) return;

      const data = await doGetReportPdf(GetReportPdfMode.attachment, testSession.id);
      if (!data) return;
      const pdfBlob = new Blob([data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `report-${testSession.id}.pdf`;
      document.body.appendChild(a);

      a.click();

      a.remove();
      URL.revokeObjectURL(pdfUrl);
    },
    [doGetReportPdf, testSession?.id]
  );

  const handlePreview = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!testSession?.id) return;

      const data = await doGetReportPdf(GetReportPdfMode.inline, testSession.id);
      if (!data) return;
      const pdfBlob = new Blob([data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      // eslint-disable-next-line i18next/no-literal-string
      window.open(pdfUrl, '_blank');
      URL.revokeObjectURL(pdfUrl);
    },
    [doGetReportPdf, testSession?.id]
  );

  if (!testSession || !testSession.id || testSession.status !== TestSessionStatus.COMPLETED) {
    return null;
  }

  return (
    <Stack direction="row" gap={spacing(1)} alignItems="center" justifyContent="flex-end" height={spacing(4)}>
      {isGettingReportPdf && <CircularProgress size={spacing(2)} />}
      {!isGettingReportPdf && (
        <IconButton
          onClick={handleDownload}
          sx={{
            '& .DocumentDownloadIcon-root': {
              width: spacing(2),
              height: spacing(2),
              color: palette.primary.main
            }
          }}
        >
          <DocumentDownloadIcon className="DocumentDownloadIcon-root" />
        </IconButton>
      )}
      {!isGettingReportPdf && (
        <IconButton
          onClick={handlePreview}
          sx={{
            '& .EyeIcon-root': {
              width: spacing(2),
              height: spacing(2),
              color: palette.primary.main
            }
          }}
        >
          <EyeIcon className="EyeIcon-root" />
        </IconButton>
      )}
    </Stack>
  );
};

export default TestSessionButtons;
